// Third-party
import React from 'react';
import type { AlignType } from 'react-bootstrap/esm/types';

// Components
import { Button } from '../../components/buttons';
import { ButtonGroup, Dropdown, Button as BootstrapButton } from 'react-bootstrap';
import { SaveComparisonModal } from '../../components/modals';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { comparisonHasItems } from '../../../utils/comparison';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import notification from '../../../utils/notification';
import Comparison from '@/types/comparison';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';

type Props = {
  user: User;
  comparison: Comparison;
  disabled?: boolean;
  onSaveComparison: (isNewlyAssigned?: boolean) => Promise<Comparison>;
  onSetComparisonName: (name: string) => void;
  onSetComparisonContact: (contact: Nullable<GenericContact>) => void;
  onSavePortfolio: (portfolio: Portfolio, contact?: GenericContact) => Promise<Portfolio>;
  onChangeItemId: (originalId: string, newId: string) => void;
};

type State = {
  showSaveComparisonModal: boolean;
  isSaving?: boolean;
  isSaved?: boolean;
};

class ComparisonSaveButton extends React.Component<Props, State> {
  readonly state: State = {
    showSaveComparisonModal: false,
  };

  validateUnassignedPortfolios = () => {
    const { comparison } = this.props;
    const unassignedPortfolios = [];

    const items = comparison.items.filter(i => !i.providedBy && i.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

    for (const item of items) {
      if ((!item.source?.contact && !item.source?.mine && !item.enterprise) || item.source?.isFromPostMessage) {
        unassignedPortfolios.push(item.source);
      }
    }

    return unassignedPortfolios;
  };

  saveComparison = async (contact?: GenericContact, isNewlyAssigned?: boolean) => {
    this.setState({ isSaving: true, isSaved: false });

    const portfoliosToPersist = this.validateUnassignedPortfolios();
    for (const portfolio of portfoliosToPersist) {
      const newPortfolio: Portfolio = {
        mine: false,
        ...portfolio!,
        riskProfile:
          portfolio?.riskProfile != null
            ? {
                ...portfolio.riskProfile,
                _id: undefined,
              }
            : undefined,
      };

      const savedPortfolio = await this.props.onSavePortfolio(newPortfolio, contact);
      if (newPortfolio._id && savedPortfolio._id && newPortfolio._id !== savedPortfolio._id) {
        this.props.onChangeItemId(newPortfolio._id, savedPortfolio._id);
      }
    }

    return this.props
      .onSaveComparison(isNewlyAssigned)
      .then(() => {
        notification.success(formatMessage('Comparison.SaveSuccess'));
        this.setState({ isSaved: true });
        this.hideSaveComparisonModal();
      })
      .finally(() => {
        this.setState({ isSaving: false });
      });
  };

  saveComparisonIsDisabled = () => {
    const { comparison, disabled } = this.props;
    const { isSaving, isSaved } = this.state;
    if (comparison.isDirty && isSaved) {
      this.setState({ isSaved: false });
    }
    return disabled || !comparisonHasItems(comparison) || !comparison.isDirty || isSaving;
  };

  saveToContactIsDisabled = () => {
    const { comparison } = this.props;
    return !!comparison.householdId;
  };

  showSaveComparisonModal = () => {
    this.setState({ showSaveComparisonModal: true });
  };

  hideSaveComparisonModal = () => {
    this.setState({ showSaveComparisonModal: false });
  };

  onSaveComparison = (name: string, contact?: GenericContact) => {
    this.hideSaveComparisonModal();
    let isNewlyAssigned = false;

    if (contact) {
      this.props.onSetComparisonContact(contact);
      isNewlyAssigned = true;
    }

    this.props.onSetComparisonName(name);

    this.saveComparison(contact, isNewlyAssigned);
  };

  getMenuItems = () =>
    [
      {
        value: formatMessage('Comparison.SaveChanges'),
        onClick: () => this.saveComparison(),
        icon: 'save',
        hidden: this.saveComparisonIsDisabled() || this.saveToContactIsDisabled(),
      },
      {
        value: formatMessage('Comparison.SaveToContact'),
        onClick: this.showSaveComparisonModal,
        icon: 'save-to',
        hidden: this.saveToContactIsDisabled(),
      },
    ].filter(i => !i.hidden);

  renderDropdownOptions = () =>
    this.getMenuItems().map(item => (
      <Dropdown.Item key={item.value} role="menuitem" as="button" onClick={() => item.onClick()}>
        <i className={`icon-${item.icon}`} />
        {item.value}
      </Dropdown.Item>
    ));

  render() {
    const { comparison, user } = this.props;
    const { showSaveComparisonModal, isSaving, isSaved } = this.state;
    const { contact } = comparison;

    const disabled = this.saveComparisonIsDisabled();

    return (
      <>
        {comparison.lastRunAt && !contact && (
          <ButtonGroup className="top-bar-button save-comparison-button-group">
            <BootstrapButton variant="secondary" className="btn-reset main-button" disabled={disabled} onClick={() => this.saveComparison()}>
              <div className="button-group-icon">
                <i className={`icon-${isSaving ? 'spinner' : 'save'}`} />
                <FormattedMessage id={isSaving ? 'Common.Saving' : 'Common.Save'} />
              </div>
            </BootstrapButton>

            {!this.saveToContactIsDisabled() && (
              <Dropdown className="save-comparison-dropdown">
                <Dropdown.Toggle id="save-comparison-dropdown" className="dropdown-container">
                  <span className="divider" />
                  <i className="icon-cheveron-down" />
                  <span className="visually-hidden">
                    <FormattedMessage id="Common.OpenOptions" />
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu as="ul" role="menu" align={'left' as AlignType}>
                  {this.renderDropdownOptions().map(option => (
                    <li key={option.key} role="presentation">
                      {option}
                    </li>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </ButtonGroup>
        )}
        {contact && (
          <Button className="top-bar-button" dark disabled={disabled} onClick={() => this.saveComparison(contact)} loading={isSaving} icon="save">
            <FormattedMessage id={isSaving ? 'Common.Saving' : disabled && isSaved ? 'Common.Saved' : 'Common.Save'} />
          </Button>
        )}

        {!contact && !comparison.lastRunAt && (
          <Button className="top-bar-button" dark disabled={disabled} onClick={this.showSaveComparisonModal} loading={isSaving} icon="save">
            <FormattedMessage id={isSaving ? 'Common.Saving' : 'Common.Save'} />
          </Button>
        )}

        {showSaveComparisonModal && (
          <SaveComparisonModal
            user={user}
            requireContact={comparison.lastRunAt && !contact}
            show={showSaveComparisonModal}
            comparison={comparison}
            onSave={this.onSaveComparison}
            onCancel={this.hideSaveComparisonModal}
          />
        )}
      </>
    );
  }
}

export default ComparisonSaveButton;
